/**
 * probePractice — a person's Probe practice picture, read from their saved
 * interviews: each examination area's latest rating and how it moved, and
 * the actions the interviews left. The web's Home and sidebar draw it, and
 * the agent's `governance_get_probe_practice` returns it, from this one
 * computation over the domain API's interview list.
 *
 * Every number comes from what the summariser wrote. An interview without a
 * debrief adds nothing to the areas and is counted as such, never guessed.
 * The API's answer is read leniently: an area, rating or moment a newer API
 * sends that this code does not know is skipped, not fatal.
 *
 * @module probePractice
 */
import {
  PROBE_AREA_ASSESSMENTS,
  PROBE_EXAMINATION_AREAS,
  PROBE_OUTCOMES,
  PROBE_REVIEW_MAX_MOMENTS,
  type ProbeAreaAssessment,
  type ProbeAreaDebrief,
  type ProbeDebrief,
  type ProbeExaminationArea,
} from "@amoreg/contracts";

/** One answer that fell short. `turn` is the answer's position in the turns as saved. */
export interface ProbeInterviewReviewMoment {
  readonly area: ProbeExaminationArea;
  readonly turn: number;
  readonly reason: string;
  readonly strongerAnswer: string;
}

/**
 * The review as it is read back. The API reads a run still pending after
 * its stale window as `failed`, so a page can offer a retry.
 */
export type ProbeInterviewReview =
  | { readonly status: "pending" | "failed" }
  | {
      readonly status: "ready";
      /** Most important first; empty when every answer held up. */
      readonly moments: ReadonlyArray<ProbeInterviewReviewMoment>;
      readonly nextStep: string;
      readonly title: string | null;
    };

/** What the practice picture reads of one saved interview. */
export interface PracticeInterview {
  readonly id: string;
  readonly title: string;
  readonly createdAt: string;
  /** Null on older interviews, or when the summariser failed. */
  readonly debrief: ProbeDebrief | null;
  /** Null on interviews saved without one. */
  readonly review: ProbeInterviewReview | null;
}

const str = (value: unknown): string => (typeof value === "string" ? value : "");

const nonBlank = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : null;

const isOneOf = <T extends string>(values: ReadonlyArray<T>, value: unknown): value is T =>
  values.some((candidate) => candidate === value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * A debrief as the API sent it: an area or rating this code does not know
 * is skipped rather than costing the whole debrief; an outcome it does not
 * know means no debrief at all.
 */
export const debriefOf = (value: unknown): ProbeDebrief | null => {
  if (!isRecord(value)) return null;
  const outcome = value["outcome"];
  if (!isOneOf(PROBE_OUTCOMES, outcome)) return null;
  const areas = (Array.isArray(value["areas"]) ? value["areas"] : []).flatMap(
    (row: unknown): ReadonlyArray<ProbeAreaDebrief> => {
      if (!isRecord(row)) return [];
      const area = row["area"];
      const assessment = row["assessment"];
      if (!isOneOf(PROBE_EXAMINATION_AREAS, area) || !isOneOf(PROBE_AREA_ASSESSMENTS, assessment)) {
        return [];
      }
      return [{ area, assessment, note: str(row["note"]).trim() }];
    },
  );
  const followUps = (Array.isArray(value["followUps"]) ? value["followUps"] : []).flatMap(
    (line: unknown) => (typeof line === "string" && line.trim().length > 0 ? [line.trim()] : []),
  );
  return { outcome, areas, followUps };
};

/**
 * A review as the API sent it, read like the debrief: a moment in an area
 * this code does not know is skipped; a ready review without a next step,
 * or a status it does not know, is no review at all.
 */
export const reviewOf = (value: unknown): ProbeInterviewReview | null => {
  if (!isRecord(value)) return null;
  const status = value["status"];
  if (status === "pending" || status === "failed") return { status };
  if (status !== "ready") return null;
  const nextStep = nonBlank(value["nextStep"]);
  if (nextStep === null) return null;
  const moments = (Array.isArray(value["moments"]) ? value["moments"] : [])
    .flatMap((row: unknown): ReadonlyArray<ProbeInterviewReviewMoment> => {
      if (!isRecord(row)) return [];
      const area = row["area"];
      const turn = row["turn"];
      const reason = nonBlank(row["reason"]);
      const strongerAnswer = nonBlank(row["strongerAnswer"]);
      if (
        !isOneOf(PROBE_EXAMINATION_AREAS, area) ||
        typeof turn !== "number" ||
        !Number.isInteger(turn) ||
        turn < 0 ||
        reason === null ||
        strongerAnswer === null
      ) {
        return [];
      }
      return [{ area, turn, reason, strongerAnswer }];
    })
    .slice(0, PROBE_REVIEW_MAX_MOMENTS);
  return { status, moments, nextStep, title: nonBlank(value["title"]) };
};

/** One interview of the API's list as the practice picture reads it; null without an id. */
export const practiceInterviewOf = (row: unknown): PracticeInterview | null => {
  if (!isRecord(row)) return null;
  const id = str(row["id"]);
  if (id.length === 0) return null;
  return {
    id,
    title: str(row["title"]) || "Probe interview",
    createdAt: str(row["createdAt"]),
    debrief: debriefOf(row["debrief"]),
    review: reviewOf(row["review"]),
  };
};

// ── Names ─────────────────────────────────────────────────────────────

/** An examination area as a heading: its first letter capitalised, the rest as the index has it. */
export const areaLabel = (area: ProbeExaminationArea): string =>
  `${area.charAt(0).toUpperCase()}${area.slice(1)}`;

/**
 * What a saved interview is called: the review's title, else the first
 * examination area its debrief rated, else the title it was saved with
 * (the examiner's opening question).
 */
export const interviewName = (
  interview: Pick<PracticeInterview, "title" | "debrief" | "review">,
): string => {
  if (interview.review?.status === "ready" && interview.review.title !== null) {
    return interview.review.title;
  }
  const area = interview.debrief?.areas[0]?.area;
  return area === undefined ? interview.title : areaLabel(area);
};

// ── Areas ─────────────────────────────────────────────────────────────

/** Where an area stands, from its most recent rating. */
export type AreaStanding = "needs-work" | "thin" | "unrehearsed" | "strong";

export interface PracticeArea {
  readonly area: ProbeExaminationArea;
  readonly standing: AreaStanding;
  readonly counts: Readonly<Record<ProbeAreaAssessment, number>>;
  /** The rating before the latest; null when rated at most once. */
  readonly previous: ProbeAreaAssessment | null;
  /** The most recent rating, with the interview it came from; null when never rated. */
  readonly latest: {
    readonly assessment: ProbeAreaAssessment;
    readonly note: string;
    readonly interviewId: string;
    readonly createdAt: string;
  } | null;
}

const STANDING_ORDER: Readonly<Record<AreaStanding, number>> = {
  "needs-work": 0,
  thin: 1,
  unrehearsed: 2,
  strong: 3,
};

const standingOf = (latest: ProbeAreaAssessment | null): AreaStanding =>
  latest === null
    ? "unrehearsed"
    : latest === "weak"
      ? "needs-work"
      : latest === "adequate"
        ? "thin"
        : "strong";

/**
 * Every examination area with its record across the debriefed interviews
 * (newest first in, most in need of practice first out): areas whose last
 * rating was weak, then thin, then never rated, then strong — and within
 * each, the most often weak first.
 */
export const practiceAreas = (
  interviews: ReadonlyArray<Pick<PracticeInterview, "id" | "createdAt" | "debrief">>,
): ReadonlyArray<PracticeArea> =>
  PROBE_EXAMINATION_AREAS.map((area): PracticeArea => {
    const counts = { strong: 0, adequate: 0, weak: 0 };
    let latest: PracticeArea["latest"] = null;
    let previous: ProbeAreaAssessment | null = null;
    for (const interview of interviews) {
      const rating = interview.debrief?.areas.find((entry) => entry.area === area);
      if (rating === undefined) continue;
      counts[rating.assessment] += 1;
      if (latest !== null && previous === null) previous = rating.assessment;
      latest ??= {
        assessment: rating.assessment,
        note: rating.note,
        interviewId: interview.id,
        createdAt: interview.createdAt,
      };
    }
    return { area, standing: standingOf(latest?.assessment ?? null), counts, previous, latest };
  }).toSorted(
    (a, b) =>
      STANDING_ORDER[a.standing] - STANDING_ORDER[b.standing] ||
      b.counts.weak - a.counts.weak ||
      PROBE_EXAMINATION_AREAS.indexOf(a.area) - PROBE_EXAMINATION_AREAS.indexOf(b.area),
  );

const ASSESSMENT_RANK: Readonly<Record<ProbeAreaAssessment, number>> = {
  weak: 0,
  adequate: 1,
  strong: 2,
};

/** How the latest rating moved from the one before; null with fewer than two. */
export const areaTrend = (entry: PracticeArea): "up" | "down" | "same" | null => {
  if (entry.latest === null || entry.previous === null) return null;
  const moved = ASSESSMENT_RANK[entry.latest.assessment] - ASSESSMENT_RANK[entry.previous];
  return moved > 0 ? "up" : moved < 0 ? "down" : "same";
};

/** How many interviews rated an area. */
export const areaInterviewCount = (entry: PracticeArea): number =>
  entry.counts.strong + entry.counts.adequate + entry.counts.weak;

/** Areas whose most recent rating was weak. */
export const weakAreaCount = (areas: ReadonlyArray<PracticeArea>): number =>
  areas.filter((entry) => entry.standing === "needs-work").length;

/** How many of the interviews carry a debrief, so a reader can say what was counted. */
export const debriefCoverage = (interviews: ReadonlyArray<Pick<PracticeInterview, "debrief">>) => ({
  debriefed: interviews.filter((interview) => interview.debrief !== null).length,
  total: interviews.length,
});

// ── Actions ───────────────────────────────────────────────────────────

/**
 * What an interview leaves to do: the review's next step and the debrief's
 * follow-ups, each trimmed, each with words in it, and each listed once.
 */
export const reviewActionsOf = (
  interview: Pick<PracticeInterview, "debrief" | "review">,
): ReadonlyArray<string> => [
  ...new Set(
    [
      ...(interview.review?.status === "ready" ? [interview.review.nextStep] : []),
      ...(interview.debrief?.followUps ?? []),
    ]
      .map((line) => line.trim())
      .filter((line) => line.length > 0),
  ),
];

/** One action an interview left, with the interview it came from. */
export interface ActionEntry {
  readonly text: string;
  readonly interviewId: string;
  readonly title: string;
  readonly createdAt: string;
}

/** The actions an interview left, as its review lists them, with the interview they came from. */
export const interviewActions = (interview: PracticeInterview): ReadonlyArray<ActionEntry> => {
  const title = interviewName(interview);
  return reviewActionsOf(interview).map((text) => ({
    text,
    interviewId: interview.id,
    title,
    createdAt: interview.createdAt,
  }));
};

/**
 * Every action the interviews left, newest interview first; the same words
 * in two interviews are listed once, from the newer.
 */
export const actionsAcross = (
  interviews: ReadonlyArray<PracticeInterview>,
): ReadonlyArray<ActionEntry> => {
  const seen = new Set<string>();
  return interviews.flatMap((interview) =>
    interviewActions(interview).filter((action) => {
      const key = action.text.toLowerCase().replace(/\s+/g, " ");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
  );
};
